import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Eye, EyeOff, Download, FileText } from "lucide-react";
import { cn } from "@/lib/utils";

import type {
  CashFlowRow,
  DisplayCurrency,
} from "@/hooks/finance/cash-flow/useCashFlow";
import { makeFormatValue } from "@/components/finance/reports/cash-flow-details-new/statement-table.utils";
import { buildPnlRows } from "./buildRows";
import { exportPnlToExcel, exportPnlToPdf } from "./exportExcel";

type Props = {
  rows: CashFlowRow[];
  periodLabels: { key: string; label: string }[];
  periodKeys: string[];
  isLoading: boolean;
  displayCurrency: DisplayCurrency;
};

const isPct = (r: CashFlowRow) => r.categoryId === "__pct__";

function formatPct(v: number): string {
  if (!Number.isFinite(v)) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

export function ProfitAndLossTable({
  rows,
  periodLabels,
  periodKeys,
  isLoading,
  displayCurrency,
}: Props) {
  const formatValue = makeFormatValue(displayCurrency);
  const [showUnmapped, setShowUnmapped] = useState(false);

  const { rows: pnlRows, unmappedRows } = useMemo(
    () => buildPnlRows(rows, periodKeys),
    [rows, periodKeys]
  );

  const renderedRows =
    showUnmapped && unmappedRows.length > 0
      ? [
          ...pnlRows,
          {
            key: "pnl-unmapped-header",
            label: "UNMAPPED OPERATING POSITIONS",
            isHeader: true,
            values: new Map<string, number>(),
          } as CashFlowRow,
          ...unmappedRows,
        ]
      : pnlRows;

  return (
    <Card className="border-none shadow-lg overflow-hidden bg-white">
      <CardHeader className="bg-slate-900 border-none pb-4">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-white text-base">
              Profit & Loss Report
            </CardTitle>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              className="h-6 text-[10px]"
              onClick={() =>
                exportPnlToExcel(
                  pnlRows,
                  unmappedRows,
                  periodLabels,
                  displayCurrency,
                  `profit-and-loss-${format(new Date(), "yyyyMMdd")}.xlsx`
                )
              }
              title="Download as Excel"
            >
              <Download className="h-3 w-3 mr-1" />
              Excel
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="h-6 text-[10px]"
              onClick={() =>
                exportPnlToPdf(
                  pnlRows,
                  unmappedRows,
                  periodLabels,
                  displayCurrency,
                  `profit-and-loss-${format(new Date(), "yyyyMMdd")}.pdf`
                )
              }
              title="Download as PDF"
            >
              <FileText className="h-3 w-3 mr-1" />
              PDF
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="h-6 text-[10px]"
              onClick={() => setShowUnmapped((s) => !s)}
              disabled={unmappedRows.length === 0}
            >
              {showUnmapped ? (
                <EyeOff className="h-3 w-3 mr-1" />
              ) : (
                <Eye className="h-3 w-3 mr-1" />
              )}
              Unmapped ({unmappedRows.length})
            </Button>
            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[9px] uppercase font-black tracking-widest">
              Accrual
            </Badge>
            <div className="px-2 py-1 rounded bg-slate-800 text-slate-300 text-[9px] font-mono select-all">
              REF: PNL-{format(new Date(), "yyMMdd")}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-32 bg-slate-50/20">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="sticky left-0 z-20 bg-slate-50 w-64 px-4 py-3 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest border-r border-slate-200">
                    Line Item
                  </th>
                  {periodLabels.map((p) => (
                    <th
                      key={p.key}
                      className="px-4 py-3 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest min-w-[120px]"
                    >
                      {p.label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {renderedRows.map((row) => (
                  <PnlRow
                    key={row.key}
                    row={row}
                    periodLabels={periodLabels}
                    formatValue={formatValue}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PnlRow({
  row,
  periodLabels,
  formatValue,
}: {
  row: CashFlowRow;
  periodLabels: { key: string; label: string }[];
  formatValue: (v: number) => string;
}) {
  if (row.isHeader) {
    return (
      <tr className="bg-slate-50/80 border-t-2 border-b border-slate-200/60">
        <td className="sticky left-0 z-10 bg-inherit px-4 py-2 border-r border-slate-200/60">
          <span className="text-[10px] font-black text-slate-900 uppercase tracking-widest">
            {row.label}
          </span>
        </td>
        {periodLabels.map((p) => (
          <td key={p.key} className="py-2 px-4" />
        ))}
      </tr>
    );
  }

  const isTotal = !!row.isTotal;
  const isCP = row.key === "pnl-cp1" || row.key === "pnl-cp2" || row.key === "pnl-ebit" || row.key === "pnl-gross-profit";
  const isGrandTotal = row.key === "pnl-ebit";
  const isPercent = isPct(row);

  return (
    <tr
      className={cn(
        "group transition-colors",
        isGrandTotal
          ? "bg-slate-900 text-white hover:bg-slate-800"
          : isCP
            ? "bg-emerald-50/60"
            : isTotal
              ? "bg-slate-50"
              : "hover:bg-slate-50/50",
        isPercent && "bg-emerald-50/30 text-emerald-800 italic"
      )}
    >
      <td
        className={cn(
          "sticky left-0 z-10 px-4 py-2 border-r shadow-[4px_0_8px_-4px_rgba(0,0,0,0.05)]",
          isGrandTotal
            ? "bg-slate-900 border-slate-700"
            : isCP
              ? "bg-emerald-50/60 border-emerald-100"
              : isPercent
                ? "bg-emerald-50/30 border-emerald-100"
                : "bg-white group-hover:bg-slate-50 border-slate-100"
        )}
      >
        <div
          className={cn(
            "text-[11px] truncate",
            isTotal ? "font-bold text-slate-900" : "text-slate-600 font-medium",
            isGrandTotal && "text-white",
            isPercent && "text-emerald-700 font-semibold text-[10px]"
          )}
          style={{ paddingLeft: row.indent ? `${row.indent * 12}px` : undefined }}
        >
          {row.label}
        </div>
      </td>

      {periodLabels.map((p) => {
        const val = row.values.get(p.key) || 0;
        const display = isPercent ? formatPct(val) : formatValue(val);
        return (
          <td
            key={p.key}
            className={cn(
              "px-4 py-2 text-right tabular-nums text-[11px] font-medium",
              isTotal && "font-black",
              isGrandTotal && "text-white py-3",
              isPercent && "font-semibold text-emerald-700",
              !isTotal && !isPercent && val < 0 && "text-rose-500",
              !isTotal && !isPercent && val > 0 && "text-emerald-600",
              !isTotal && !isPercent && val === 0 && "text-slate-300 font-normal"
            )}
          >
            {display}
          </td>
        );
      })}
    </tr>
  );
}
